import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Download, Play, Layers, Database, Zap, Brain, Cpu, BarChart3 } from 'lucide-react';
import { images } from '../data/mock';
import { toast } from '../hooks/use-toast';

const TechnologySection = () => {
  const msmlPipeline = [
    { icon: Brain, label: 'Input Data', color: 'text-red-400' },
    { icon: BarChart3, label: 'Multi-Structure Analysis', color: 'text-yellow-400' },
    { icon: Cpu, label: 'Multi-Layer Processing', color: 'text-blue-400' },
    { icon: Zap, label: 'Density Optimization', color: 'text-green-400' },
    { icon: Database, label: 'Optimized Output', color: 'text-cyan-400' }
  ];

  const msmlFeatures = [
    {
      title: 'Multi-Structure',
      description: 'Handles diverse data types including neural networks, media files, and structured data.',
      benefit: 'Universal compatibility',
      icon: Layers
    },
    {
      title: 'Multi-Layer',
      description: 'Progressive optimization with multiple processing layers for maximum efficiency.',
      benefit: 'Adaptive optimization',
      icon: Database
    },
    {
      title: 'Data Formalization',
      description: 'Standardizes data representation across different domains and applications.',
      benefit: 'Seamless integration',
      icon: Zap
    }
  ];

  const technicalAdvantages = [
    {
      metric: 'Up to 30x+',
      label: 'Data type dependant Data volume reduction',
      description: 'Significantly reduces storage and transmission requirements'
    },
    {
      metric: '3x',
      label: 'Performance Improvement',
      description: 'Superior efficiency compared to single-method approaches'
    },
    {
      metric: 'up to 75%',
      label: 'Computing Load Reduction',
      description: 'Dramatically lowers computational demands'
    },
    {
      metric: '≥98.9%',
      label: 'Fidelity Preservation',
      description: 'Quality preserved on standard benchmarks (avg change ≤0.5 pp)'
    }
  ];

  return (
    <section id="technology" className="py-20 bg-slate-900 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 opacity-30">
        <img
          src={images.abstract}
          alt="Abstract Technology"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/95 to-purple-900/30" />
      </div>

      {/* Animated Matrix Background */}
      <div className="absolute inset-0">
        {Array.from({ length: 50 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-0.5 h-8 bg-gradient-to-b from-purple-400/50 to-transparent"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -100, 100, 0],
              opacity: [0, 0.7, 0.3, 0]
            }}
            transition={{
              duration: Math.random() * 6 + 4,
              repeat: Infinity,
              delay: Math.random() * 3
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">MSML</span> Technology
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Multi Structure Multi Layer data formalization - Advanced data density optimization that maximizes value per byte
          </p>
        </motion.div>

        {/* Technical Performance Metrics */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16"
        >
          {technicalAdvantages.map((advantage, index) => (
            <motion.div
              key={advantage.label}
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <Card className="bg-slate-800/50 border-purple-400/20 hover:border-purple-400/40 transition-all">
                <CardContent className="p-6">
                  <div className="text-3xl font-bold text-purple-400 mb-2">{advantage.metric}</div>
                  <div className="text-white font-semibold mb-2">{advantage.label}</div>
                  <div className="text-sm text-gray-400">{advantage.description}</div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>

        {/* Company Introduction */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="max-w-5xl mx-auto">
            <div className="bg-slate-800/50 rounded-2xl p-8 border border-purple-400/20 backdrop-blur-sm">
              <div className="space-y-6 text-gray-300 leading-relaxed">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-4">What is MSML?</h3>
                  <p className="text-lg">
                    MSML (Multi‑Structure, Multi‑Layered data formalization) is a patented methodology for representing complex signals by factorizing them across structures and scales. In plain terms: we keep the meaning‑bearing components and minimize redundancies, so more intelligence fits into fewer bytes without losing what matters to human perception or machine understanding.
                  </p>
                </div>
                
                <div className="border-t border-gray-600 pt-6">
                  <h3 className="text-2xl font-bold text-white mb-4">The principle behind everything: IDDO</h3>
                  <p className="text-lg">
                    Intelligent Data Density Optimization (IDDO) is the brand and operating principle built on our issued patents covering multi‑structural, multi‑level information formalization. Practically, it means factorizing signals so the parts that carry meaning are preserved and redundancies minimized. The result is higher information density without loss of semantic fidelity, shorter time‑to‑first‑word, lower bitrates at the same perceived quality, and fewer GPU cycles per task across audio, video, and multimodal datasets (including LLMs).
                  </p>
                </div>

                <div className="border-t border-gray-600 pt-6">
                  <h4 className="text-xl font-semibold text-white mb-4">General Harmonics</h4>
                  <p className="text-lg">
                    We are a research corporation with solid scientific background that develops AI-powered Intelligent Data Density Optimization custom products and solutions. Our foundation rests on patented MSML (Multi-Structural, Multi-Level) principles – a method for factorizing complex data signals while preserving meaning and cutting redundancy.
                  </p>
                </div>
                
                <p className="text-lg">
                  MSML fundamentals are applicable across large arrays of market sectors, including AI and LLMs, Media, Telecom and Edge Computing, AV and Robotics and Autonomous systems. For each sector, we provide whitepapers outlining comprehensive use case analysis of the benefits, supported by an array of demonstrable digital products and solutions for the Media industry that provide solid technology validation ground.
                </p>
                
                <div className="border-t border-gray-600 pt-6 mt-6">
                  <h4 className="text-xl font-semibold text-white mb-4">MSML Intellectual Property Portfolio</h4>
                  
                  <div className="space-y-4 text-base">
                    <div className="bg-slate-700/30 rounded-lg p-4">
                      <span className="font-semibold">a. MSML-LLMC Framework:</span> Large Language Model optimization mathematical framework enabling IDDO capabilities for LLMs and multimodal data, maximizing fidelity per byte while minimizing compute and storage.
                    </div>
                    
                    <div className="bg-slate-700/30 rounded-lg p-4">
                      <span className="font-semibold">b. D1AUDIO Technology:</span> IDDO principle drives our fidelity lossless codec with programmable media container, storing and streaming richer content at lower bitrates.
                    </div>
                    
                    <div className="bg-slate-700/30 rounded-lg p-4">
                      <span className="font-semibold">c. PulsBeat Media Products:</span> AudioMine immersive tools, My.ARTIST.one ecosystem, and Connect360 engagement hub designed for better searchability, personalization, and monetization.
                    </div>
                    
                    <div className="bg-slate-700/30 rounded-lg p-4">
                      <span className="font-semibold">d. Infrastructure & Security:</span> Cognitive Dynamic Media Cloud (cDM Cloud), LiveLink and Logical Data Embroidering (LDE) for content authenticity, cryptographic signing, and traceable lineage enhancing data sovereignty.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* MSML Data Density Optimization Pipeline */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="bg-slate-800/50 rounded-3xl p-8 backdrop-blur-sm border border-cyan-400/20">
            <h3 className="text-2xl font-bold text-white mb-8 text-center">MSML Data Density Optimization Pipeline</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6 items-center">
              {msmlPipeline.map((step, index) => {
                const IconComponent = step.icon;
                return (
                  <div key={step.label} className="text-center">
                    <motion.div
                      className={`w-16 h-16 mx-auto mb-4 rounded-full bg-slate-700 flex items-center justify-center ${step.color}`}
                      whileHover={{ scale: 1.1, rotate: 360 }}
                      transition={{ duration: 0.3 }}
                    >
                      <IconComponent size={24} />
                    </motion.div>
                    <p className="text-gray-300 text-sm font-medium">{step.label}</p>
                    {index < 4 && (
                      <motion.div
                        className="hidden md:block absolute mt-8 w-12 h-0.5 bg-gradient-to-r from-cyan-400 to-blue-400"
                        style={{ left: '80%', transform: 'translateX(-50%)' }}
                        animate={{ scaleX: [0, 1] }}
                        transition={{ delay: index * 0.3, duration: 0.5 }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </motion.div>

        {/* MSML Core Features */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16"
        >
          {msmlFeatures.map((feature, index) => {
            const IconComponent = feature.icon;
            const colors = ['purple', 'pink', 'cyan'];
            const colorClass = colors[index];

            return (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.2, duration: 0.5 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05, y: -10 }}
              >
                <Card className={`h-full bg-slate-800/50 border-${colorClass}-400/20 hover:bg-slate-700/50 transition-all duration-300`}>
                  <CardHeader>
                    <div className="flex items-center space-x-3 mb-2">
                      <motion.div
                        className={`w-12 h-12 rounded-full bg-${colorClass}-400/20 flex items-center justify-center`}
                        whileHover={{ rotate: 360 }}
                        transition={{ duration: 0.5 }}
                      >
                        <IconComponent className={`text-${colorClass}-400`} size={24} />
                      </motion.div>
                      <CardTitle className="text-white">{feature.title}</CardTitle>
                    </div>
                    <CardDescription className="text-gray-300">
                      {feature.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Badge 
                      variant="outline" 
                      className={`border-${colorClass}-400/30 text-${colorClass}-400`}
                    >
                      {feature.benefit}
                    </Badge>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <div className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 rounded-2xl p-8 border border-purple-400/20">
            <h3 className="text-2xl font-bold text-white mb-6">Learn More About MSML Technology</h3>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700"
                onClick={() => toast({
                  title: "Document Access Restricted",
                  description: "Requested document requires signed NDA, please contact the company through the correspondent contact form.",
                  duration: 5000
                })}
              >
                <Download className="mr-2" size={20} />
                Download Technical Whitepaper
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="border-purple-400 text-purple-400 hover:bg-purple-400 hover:text-white"
                onClick={() => toast({
                  title: "Document Access Restricted", 
                  description: "Requested document requires signed NDA, please contact the company through the correspondent contact form.",
                  duration: 5000
                })}
              >
                <Play className="mr-2" size={20} />
                Watch Technology Demo
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TechnologySection;